/* Core & libraries */
import React, {useEffect, useState} from "react";
import { FormattedMessage } from "react-intl";
import {useNavigate} from "react-router";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch }  from "../endpoints/base";
import {
  DELETE_PEOPLE_ENDPOINT,
  MEMBERS_GRID_ENDPOINT,
  TEAMS_GRID_ENDPOINT,
  CREATE_TEAM_ENDPOINT,
  EDIT_TEAM_ENDPOINT,
  DELETE_TEAM_ENDPOINT,
  SETDEFAULT_TEAM_ENDPOINT,
  TEAM_DETAILS_ENDPOINT,
  TEAM_COUNT_ENDPOINT,
  TEAM_MEMEMERS_COUNT_ENDPOINT,
} from '../endpoints/endpoints';
import { useNotificationContext } from "../contexts/notificationContext";
import { PEOPLE_TEAMS_ROUTE } from "../constants/routes";
import { useUserContext } from "../contexts/userContext";


/* Components */
/* Styles */

export const useTeamService = (props = {}) => {
  const {onError, skipUseEffect = false } = props;
  const [teamsResponse, setTeamsResponse] = useState({})
  const [teamDetails, setTeamDetails] = useState({})
  const [membersList, setMembersList] = useState({})
  const { genericApiFetch } = useGenericApiFetch();
  const { setNotification } = useNotificationContext();
  const navigate = useNavigate();
  const userDetails = useUserContext();
  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  useEffect(() => {
    if (skipUseEffect) {
      (async () => {
        await getTeamsList();
      })();
    }
  }, []);

  const getTeamsList = async (options) => {
    if (options) {
      const response = await genericApiFetch({
        url: TEAMS_GRID_ENDPOINT.replace("{companyEntityId}", companyEntityId),
        method: "GET",
        queryParams: options,
        spinner: "teamsGridSpinner"
      });
      if (response.success) {
        setTeamsResponse(response.data);
      } else {
        setTeamsResponse({ results: null });
      }
    }
  };

  const getTeamDetails = async (teamId) => {
    const response = await genericApiFetch({
      url: TEAM_DETAILS_ENDPOINT.replace("{groupEntityId}", teamId),
      method: "GET",
    });
    if (response.success) {
      setTeamDetails(response.data);
    }
  };

  const getMembersList = async (options, teamId) => {
    if (options) {
      const response = await genericApiFetch({
        url: MEMBERS_GRID_ENDPOINT.replace("{groupEntityId}", teamId),
        method: "GET",
        queryParams: options,
        spinner: "membersGridSpinner"
      });
      if (response.success) {
        setMembersList(response.data);
      } else {
        setMembersList({ results: null });
      }
    }
  };

  const createTeam = async (teamData) => {
    const response = await genericApiFetch({
      url: CREATE_TEAM_ENDPOINT,
      method: "POST",
      data : teamData,
      spinner: "createTeamSpinner"
    })
    if (response.success) {
      return response.data;
    }
    onError();
    return false;
  };

  const deleteTeam = async (teamId) => {
    const response = await genericApiFetch({
      url: DELETE_TEAM_ENDPOINT.replace("{groupEntityId}",teamId),
      method: "DELETE",
      spinner: "deleteTeamSpinner"
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="deleteTeam.notificationTitle" />,
        <FormattedMessage id="deleteTeam.notificationMessage" values={{groupName: response.data.group.groupName}}/>
      )
      navigate('../' + PEOPLE_TEAMS_ROUTE);
      return true;
    }
  }

  const setDefaultTeam = async (teamId) => {
    const response = await genericApiFetch({
      url: SETDEFAULT_TEAM_ENDPOINT.replace("{groupEntityId}",teamId),
      method: "POST",
      data: {},
      spinner: "setDefaultTeamSpinner"
    })
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="setDefaultTeam.notificationTitle" />,
        <FormattedMessage id="setDefaultTeam.notificationMessage" values={{defaultTeam: response.data.group.groupName}}/>
      )
      await getTeamDetails(teamId)
      return true
    }
    return false
  };

  const deleteMember = async (entity) => {
    const response = await genericApiFetch({
      url: DELETE_PEOPLE_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{userId}", entity.entityID),
      method: "DELETE",
      spinner: "deleteActionSpinner"
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notification.deleteSuccessTitle" />,
        <FormattedMessage id="notification.deleteSuccessText" values={{name: `${entity?.lastName} ${entity?.firstName}`}}/>
      )
      return true;
    }
    return false;
  }
  const editTeam = async (teamData) => {
    const response = await genericApiFetch({
      url: EDIT_TEAM_ENDPOINT.replace("{groupEntityId}",teamData.teamId),
      method: "PUT",
      data : teamData,
      spinner: "editTeamSpinner"
    })

    if (response.success) {
      await getTeamDetails(teamData.teamId)
      return response;
    }
    return response;
  };

  const getTeamsCount = async () => {
    const countResponse = await  genericApiFetch({
      url: TEAM_COUNT_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "GET",
    })
    if (countResponse.success) {
      return countResponse?.data?.groupCount;
    }
  };
  const getTeamMembersCount = async (teamId) => {
    const countResponse = await  genericApiFetch({
      url: TEAM_MEMEMERS_COUNT_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{groupEntityId}", teamId),
      method: "GET",
    })
    if (countResponse.success) {
      return countResponse?.data?.groupMemberCount;
    }
  };

  return {
    teamsResponse,
    teamDetails,
    membersList,
    getTeamsList,
    getTeamDetails,
    getMembersList,
    getTeamMembersCount,
    getTeamsCount,
    deleteTeam,
    deleteMember,
    createTeam,
    setDefaultTeam,
    editTeam
  };
}

